/**
 * auth.service.ts
 * Secured Communications
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import { deserialize, serialize } from "bson";
import { DateTime } from "luxon";
import moment from "moment-timezone";
import { jwtDecode, JwtPayload } from "jwt-decode"; //jsonwebtoken is server-side only

import { Account, Membership, Device, AccountInvite, User, ThirdPartyAuth, AuthType, UserScopes } from "../models/auth";
import { InviteRequest, InviteResponse } from "../models/requests";

import { http } from "./http.service";
import { http as bHttp } from "./binaryhttp.service";
import { cryptoServiceFactory } from "./crypto.service";
import { credServiceFactory, SaveState } from "./db.service";
import { bufferToBase64 } from "./encoding.service";
import { ExpiredTokenError } from "../models/error";

export interface SecuredRegion {
  id: string,
  region_name: string,
  domain: string
}

export interface NewUser extends User {
  pass: string,
  confirmpass: string,
  publicKey?: string,
  disableEmail?: boolean
}

interface AnonMeetingMemberBody {
  clientID: string,
  clientSecret: string,
  meetingID: string,
  meetingToken: string,
  firstname: string,
  lastname: string,
  publicKey: string
}

export interface ChallengeResponse {
  challenge: Uint8Array,
  auths: Array<ThirdPartyAuth>,
  rp: {
    name: string,
    id: string
  }
  token: string
}

export interface ServerContextResponse {
  regions: Array<SecuredRegion>,
  deployVersion: string,
  minAppBundle: number,
  opentokApiKey: string,
  usNumbers: Array<string>,
  ukNumbers: Array<string>,
  auNumbers: Array<string>,
  caNumbers: Array<string>
}

interface QueryOptions {
  limit?: string,
  q?: string
}

export interface AuthService {
  readonly domain: string,
  readonly accessToken: string,
  readonly OPENTOK_API_KEY: string,
  readonly US_PHONES: Array<string>,
  readonly UK_PHONES: Array<string>,
  readonly AU_PHONES: Array<string>,
  readonly CA_PHONES: Array<string>,
  setAccessToken(token: string): void,
  currentAccountMembership: Membership | null | undefined,
  readonly tz: string,
  //region: SecuredRegion,
  readonly regions: { [id: string]: SecuredRegion },
  readonly needAuthMethod: boolean,
  init(forUserID?: string): Promise<string | undefined>,
  login(email: string, password: string, otp: string): Promise<TokenData>,
  logout(): void,
  didLogout(): Promise<void>,
  getMe(): Promise<User>,
  //getServerContext (): Promise<ServerContextResponse>,
  getAccountMemberships(force?: boolean): Promise<Array<Membership>>,
  sendInvite(req: InviteRequest): Promise<InviteResponse>,
  validateToken(token: string): Promise<AccountInvite>,
  validateInviteCode(code: string): Promise<{ invite: AccountInvite, token: string }>,
  getAccountInvites(accountID: string): Promise<Array<AccountInvite>>,
  createUser(newuser: NewUser, accountID: string, inviteToken: string): Promise<any>,
  claimInvite(inviteToken: string): Promise<void>,
  deleteInvite(accountID: string, inviteID: string): Promise<void>,
  claimOneTimeToken(token: string): Promise<void>,
  createTempUser(accountID: string, meetingID: string, meetingToken: string,
    firstname: string, lastname: string): Promise<void>,
  createTempMember(accountID: string, meetingID: string, meetingToken: string): Promise<Membership>,
  getAccountMembers(search?: string, limit?: number): Promise<Array<Membership>>,
  getAccountMembersForGroup(groupID: string, search?: string, limit?: number): Promise<Array<Membership>>,
  memberFor(userID: string): Promise<Membership | null>,
  membersFor(userIDs: Set<string>): Promise<{ [id: string]: Membership }>,
  updateMembership(forUserID: string, membership: Partial<Membership>): Promise<Membership>,
  deleteMembership(forUserID: string): Promise<void>,
  getMemberDevices(memberID: string): Promise<Array<Device>>,
  deleteDevice(deviceID: string): Promise<void>,
  updateDeviceName(deviceID: string, name: string): Promise<{ id: string, displayName: string }>,
  createLocalCredential(userID: string, email: string): Promise<ThirdPartyAuth>,
  assertLocalCredential(auths: Array<ThirdPartyAuth>,
    challenge: ArrayBuffer, rpID: string, token: string): Promise<void>,
  getAuthOptions(email: string): Promise<ChallengeResponse>,
  getOTPSecret(type: "authenticator", email: string): Promise<{ uri: string, secret: string }>,
  confirmOTP(otpType: "authenticator", email: string, otp: string, secret: string): Promise<ThirdPartyAuth>,
  updateUserValue(key: string, value: string): Promise<void>,
  changePassword(current: string, newpass: string): Promise<void>,
  sendPWRequest(email: string): Promise<void>,
  checkPWRequirements(possiblePass: string): string,
  addEmail(email: string): Promise<void>,
  removeEmail(email: string): Promise<void>,
  updateAuth(authID: string, auth: { authname: string }): Promise<void>
  removeAuth(authID: string): Promise<void>,
  uploadAvatar(membership: Membership, contentType: string, imageData: Uint8Array): Promise<void>,
  makeAvatarText(acctMember: Membership | User): string,
  getAccountInfo(accountID: string): Promise<Account>,
  updateAccountInfo(accountID: string, update: Partial<Account>): Promise<Account>
  authorizeOAuth(
    redirectURI: string,
    clientID: string,
    selectedAccount: string,
    state: string,
    codeChallenge: string | null,
    codeChallengeMethod: string | null): Promise<{ redirectUri: string, code: string, } | undefined>
}

class AuthServiceImpl implements AuthService {

  protected static TOKEN_KEY_PREFIX = "sc.auth.";
  protected static MEMBERSHIP_KEY = "sc.memberships";
  //protected static CUR_ACCOUNT_KEY = "sc.currentaccount";
  //protected static SYNC_KEY = "sc.synctime";

  private refresh_token: string;
  private access_token: string;
  private tokenExpires: moment.Moment;
  public needAuthMethod: boolean;
  private currentRegion: SecuredRegion;
  private currentMembership: Membership | null | undefined;

  private lastSync: moment.Moment;
  private memberships: Array<Membership>;

  private acctMemberMap: { [id: string]: Membership };
  private acctMemberTasks: { [id: string]: Promise<Membership | null> };

  private refreshJob: Promise<string | null> | null;

  public OPENTOK_API_KEY: string;
  public US_PHONES: string[];
  public UK_PHONES: string[];
  public AU_PHONES: string[];
  public CA_PHONES: string[];

  public get domain(): string {
    return http.currentDomain();
  }
  public get accessToken(): string {
    return this.access_token;
  }
  public setAccessToken(token: string): void {
    const decoded = this.unauthDecode(token);
    if (!decoded.exp) {
      throw Error("Couldn't determine token expiration");
    }
    if (Date.now() >= decoded.exp * 1000) {
      throw Error("That token has expired. Please, try again.");
    }
    this.access_token = token;
    this.tokenExpires = moment(decoded.exp * 1000);
    console.log("token expires:", this.tokenExpires.format());
  }

  public set region(newVal: SecuredRegion) {
    this.setRegion(newVal);
  }
  public get region(): SecuredRegion {
    return this.currentRegion;
  }
  public get regions(): { [id: string]: SecuredRegion } {
    return secrets.REGIONS;
  }

  get currentAccountMembership(): Membership | null | undefined {
    return this.currentMembership;
  }
  set currentAccountMembership(newVal: Membership | null | undefined) {
    if (this.currentMembership && this.currentMembership._id == newVal?._id) {
      return;
    }
    const oldUserID = this.currentMembership?.userID;
    const userID = newVal?.userID;
    this.currentMembership = newVal;
    if (newVal) {
      this.setRegion(secrets.REGIONS[newVal.regionID]);
    }
    const updater = (state: SaveState | null | undefined): Promise<SaveState | null> => {
      if (state) {
        state.curMembershipID = newVal?._id || "";
      }
      return bluebird.resolve(state);
    };

    if (oldUserID && (oldUserID !== userID)) {
      // this includes case where userID does not exist
      credServiceFactory().updateUserState(oldUserID, updater);
    }
    if (userID) {
      credServiceFactory().updateUserState(userID, updater);
    }
  }

  get tz(): string {
    return this.currentMembership?.timezone || "";
  }

  constructor() {
    this.refresh_token = "";
    this.access_token = "";
    this.tokenExpires = moment().subtract(30, "minutes");
    this.lastSync = moment().subtract(3, "hours");
    this.memberships = [];
    this.currentMembership = null;
    this.acctMemberMap = {};
    this.acctMemberTasks = {};
    this.refreshJob = null;
    this.OPENTOK_API_KEY = "";
    this.US_PHONES = [];
    this.UK_PHONES = [];
    this.AU_PHONES = [];
    this.CA_PHONES = [];
    this.needAuthMethod = false;

    this.refresh_middleware = this.refresh_middleware.bind(this);

    http.setTokenFactory(this.refresh_middleware);
    bHttp.setTokenFactory(this.refresh_middleware);

    const domain = typeof window === "undefined" ? self.location.host : window.location.host;
    this.currentRegion = secrets.REGIONS[Object.keys(secrets.REGIONS)[0]];
    this.currentRegion && http.setDomain(this.currentRegion.domain);
    for (const regionKey in secrets.REGIONS) {
      const r = secrets.REGIONS[regionKey];
      if (!this.currentRegion || r.domains && r.domains.includes(domain) ||
        r.domain && r.domain === domain) {
        this.currentRegion = r;
        http.setDomain(domain);
      }
    }
  }

  async init(forUserID?: string): Promise<string | undefined> {
    if (forUserID && this.currentMembership && this.currentMembership.userID === forUserID) {
      return forUserID;
    }
    const [userID,] = await bluebird.all([
      this.loadAuthData(this.currentRegion, forUserID),
      this.getServerContext(),
    ]);

    if (userID) {
      const [saveState,] = await bluebird.all([
        this.loadSaveState(userID),
        this.loadMemberships(userID)
      ]);

      this.currentMembership = null;
      if (saveState) {
        this.lastSync = moment(saveState.lastSync);
        this.currentMembership = this.memberships.find(m => m._id === saveState.curMembershipID) || null;
      }
      //console.log("auth init:", this.lastSync.format(), "saveState:", saveState, this.currentMembership);
    }
    return userID;
  }

  async authorizeOAuth(redirectURI: string, clientID: string, selectedAccount: string, state: string, codeChallenge: string | null, codeChallengeMethod: string | null): Promise<{ redirectUri: string, code: string } | undefined> {
    //hardcoded scopes for now
    const SCOPE = "profile meetings";
    try {
      const res = await http.post<any, { redirectUri: string, code: string }>("/auth/",
        {
          client_id: clientID,
          response_type: "code",
          scope: SCOPE,
          redirect_uri: redirectURI,
          state: state,
          account_id: selectedAccount,
          code_challenge: codeChallenge,
          code_challenge_method: codeChallengeMethod
        }
      )
      return res;
    } catch (error) {
      console.error("Error authorizing OAuth:", error);
      return undefined;
    }
  }


  async getAccountMemberships(force = false): Promise<Array<Membership>> {
    const halfHourAgo = moment().subtract(30, "minutes");
    if (!force && this.lastSync && this.lastSync.isAfter(halfHourAgo)) {
      return this.memberships;
    }
    const memberships = await http.get<Array<Membership>>("/account/membership/");

    let userID = "";
    for (const mem of memberships) {
      mem.regionID = this.currentRegion.id;
      userID = mem.userID;
      this.setButtonText(mem);
      mem.avatarText = this.makeAvatarText(mem);
    }

    const remaining = this.memberships.filter(m => m.regionID !== this.currentRegion.id);

    this.memberships = remaining.concat(memberships);
    this.lastSync = moment();

    if (this.currentMembership === null && this.memberships.length > 0) {
      this.currentMembership = this.memberships[0];
    }

    await this.saveMembershipsAndState(userID, this.currentMembership, memberships);

    return this.memberships;
  }

  setButtonText(membership: Membership) {
    if (membership.accountname) {
      const words = membership.accountname.split(" ");
      if (words.length >= 2) {
        membership.buttonText = words[0].charAt(0) + words[1].charAt(0);
      } else if (words.length >= 1) {
        membership.buttonText = words[0].substring(0, 2);
      }
    } else {
      membership.buttonText = membership.accountID.charAt(0);
    }
  }

  loadSaveState(userID: string): Promise<SaveState> {
    const credService = credServiceFactory();
    return credService.getUserState(userID);
  }

  async loadMemberships(userID: string): Promise<void> {

    const credService = credServiceFactory();

    const data: any = typeof window !== "undefined" && "localStorage" in window &&
      window.localStorage.getItem(AuthServiceImpl.MEMBERSHIP_KEY);
    if (data) {
      this.memberships = JSON.parse(data);
      await credService.saveMemberships(this.memberships);
      window.localStorage.removeItem(AuthServiceImpl.MEMBERSHIP_KEY);
    } else {
      this.memberships = await credService.getMemberships(userID);
    }
  }

  async saveMembershipsAndState(userID: string, currentMembership: Membership | null | undefined, memberships: Array<Membership>): Promise<void> {
    const credService = credServiceFactory();
    await bluebird.all([
      credService.saveMemberships(memberships),
      userID ? credService.updateUserState(userID, state => {
        if (state) {
          state.lastSync = new Date();
          state.curMembershipID = currentMembership?._id || ""
        } else {
          state = {
            userID: userID,
            lastSync: new Date(),
            curMembershipID: currentMembership?._id || ""
          }
        }
        return bluebird.resolve(state);
      }) : bluebird.resolve()
    ]);
  }

  getMe(): Promise<User> {
    return http.get<User>("/auth/me");
  }

  async login(email: string, password: string, otp: string): Promise<TokenData> {
    const cryptoService = cryptoServiceFactory();
    const keys = cryptoService.generateNewKeyPair();
    const result = await http.post<any, TokenData>("/auth/login/", {
      username: email,
      password: password,
      otp: otp,
      client_id: secrets.SC_CLIENT_ID,
      client_secret: secrets.SC_CLIENT_SECRET,
      publicKey: bufferToBase64(cryptoService.publicKeyToBuffer(keys.publicKey))
    });
    await this.clearAuthData(this.currentRegion);
    if (result.enc) {
      const data = JSON.parse(new TextDecoder().decode(
        cryptoService.decapsulate(result.enc, keys.privateKey))) as TokenData;
      await this.processToken(data, false);
      return data;
    } else {
      await this.processToken(result, false);
      return result;
    }
  }

  async getServerContext(): Promise<ServerContextResponse> {
    const ctx = await http.get<ServerContextResponse>("/context/");
    //console.log("server context:", ctx);
    this.OPENTOK_API_KEY = ctx.opentokApiKey;
    this.US_PHONES = ctx.usNumbers;
    this.UK_PHONES = ctx.ukNumbers;
    this.AU_PHONES = ctx.auNumbers;
    this.CA_PHONES = ctx.caNumbers;
    return ctx;
  }

  async refreshAccessToken(): Promise<string | null> {
    const cryptoService = cryptoServiceFactory();
    await cryptoService.init();
    const keys = cryptoService.generateNewKeyPair();
    const result = await http.post<any, any>("/auth/token", {
      refresh_token: this.refresh_token,
      grant_type: "refresh_token",
      scope: "profile acct profiles member groups files meetings archive templates accounts",
      client_id: secrets.SC_CLIENT_ID,
      client_secret: secrets.SC_CLIENT_SECRET,
      publicKey: bufferToBase64(cryptoService.publicKeyToBuffer(keys.publicKey))
    });
    if (result.enc) {
      const data = JSON.parse(new TextDecoder().decode(
        cryptoService.decapsulate(result.enc, keys.privateKey))) as TokenData;
      await this.processToken(data, this.needAuthMethod);
      return data.access_token;
    } else {
      await this.processToken(result, this.needAuthMethod);
      return result.access_token;
    }
  }
  async refresh_middleware(path: string): Promise<string | null> {
    const now = moment.tz();

    if (this.tokenExpires && now.isBefore(this.tokenExpires)) {
      return this.access_token;
    }
    if (!this.refresh_token) {
      return null;
    }


    if (
      path === "/auth/token" ||
      path === "/context/" ||
      path === "/authn/challenge" ||
      path === "/auth/login/") {
      return null;
    }
    if (this.refreshJob) {
      return await this.refreshJob;
    }
    this.refreshJob = this.refreshAccessToken().finally(() => {
      this.refreshJob = null;
    });
    return await this.refreshJob;
  }

  processToken(result: TokenData, needAuthentication: boolean): Promise<void> {
    this.access_token = result.access_token;
    this.refresh_token = result.refresh_token;
    this.tokenExpires = moment.tz().add(result.expires_in, "seconds");
    this.needAuthMethod = needAuthentication;

    const decoded = this.unauthDecode(result.access_token) as JwtPayload & { user: string, perms: UserScopes };

    return credServiceFactory().saveTokens({
      _id: decoded.user,
      userID: decoded.user,
      regionID: this.currentRegion.id,
      access_token: result.access_token,
      refresh_token: result.refresh_token,
      expires: this.tokenExpires.toDate(),
      received: new Date(),
      perms: decoded.perms,
      needAuthMethod: needAuthentication
    });
  }

  async loadAuthData(region: SecuredRegion, userID?: string): Promise<string | undefined> {
    const key = AuthServiceImpl.TOKEN_KEY_PREFIX + region.id;

    const credService = credServiceFactory();
    //let userID: string | null = null;

    this.refresh_token = "";
    this.access_token = "";
    this.tokenExpires = moment().subtract(30, "minutes");

    let data: any = typeof window !== "undefined" && "localStorage" in window &&
      window.localStorage.getItem(key);
    if (data) {
      // old token storage location
      data = JSON.parse(data);
      this.refresh_token = data.refresh_token;
      this.access_token = data.access_token;
      this.tokenExpires = moment.tz(data.expires, moment.ISO_8601, "UTC");

      const decoded = this.unauthDecode(
        data.access_token) as JwtPayload & { user: string, perms: UserScopes };

      userID = decoded.user;
      try {
        await credService.saveTokens({
          _id: userID,
          userID: userID,
          regionID: this.currentRegion.id,
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires: this.tokenExpires.toDate(),
          received: new Date(),
          perms: decoded.perms,
          needAuthMethod: false
        });

        window.localStorage.removeItem(key);
      } catch (err) {
        console.error("Error saving tokens:", err);
      }
      return userID;
    }

    const tokens = await credService.getTokens(this.currentRegion.id, userID);
    console.log("loadAuthData, tokens:", tokens);

    if (tokens.length > 0) {
      const tokenData = tokens[0];
      const decoded = this.unauthDecode(
        tokenData.access_token) as JwtPayload & { user: string, perms: UserScopes, s: string };
      userID = decoded.user;
      this.refresh_token = tokenData.refresh_token;
      this.access_token = tokenData.access_token;
      this.tokenExpires = moment(tokenData.expires);
      this.needAuthMethod = tokenData.needAuthMethod;
    }
    return userID;
  }

  setRegion(newVal: SecuredRegion) {
    if (this.currentRegion && this.currentRegion.id === newVal.id) {
      return;
    }
    this.currentRegion = newVal;
    http.setDomain(newVal.domain);
    this.loadAuthData(this.currentRegion);
  }

  async logout(): Promise<void> {
    await bluebird.all([
      await http.post<any, any>("/auth/logout", {}),
      this.clearAuthData(this.currentRegion)
    ]);
  }

  async didLogout(): Promise<void> {
    this.tokenExpires = moment().subtract(30, "minutes");
    this.lastSync = moment().subtract(3, "hours");
    this.memberships = [];
    this.currentMembership = null;
    this.refresh_token = "";
    this.access_token = "";
  }
  async clearAuthData(region: SecuredRegion): Promise<void> {
    const userID = this.currentMembership?.userID;
    //window.localStorage.removeItem(AuthServiceImpl.SYNC_KEY);
    //window.localStorage.removeItem(AuthServiceImpl.MEMBERSHIP_KEY);
    //window.localStorage.removeItem(AuthServiceImpl.CUR_ACCOUNT_KEY);
    this.tokenExpires = moment().subtract(30, "minutes");
    this.lastSync = moment().subtract(3, "hours");
    this.memberships = [];
    this.currentMembership = null;
    this.refresh_token = "";
    this.access_token = "";

    const key = AuthServiceImpl.TOKEN_KEY_PREFIX + region.id;

    // service worker doesn't have access to localStorage
    if (typeof window !== "undefined" && "localStorage" in window) {
      window.localStorage.removeItem(key);
    }

    const credService = credServiceFactory();
    if (userID) {
      return bluebird.all([
        credService.deleteTokens(userID),
        credService.deleteUserState(userID),
        credService.deleteMemberships(userID)
      ]).then(null, err => {
        console.error("Error clearing tokens or state for user:", userID, err);
      });
    } else {
      console.log("logging out every user in region:", region.id);
      return credService.deleteAllTokens(region.id).then(null, err => {
        console.error("Error clearing tokens for region:", region, err);
      });
    }
  }

  sendPWRequest(email: string): Promise<void> {
    return http.post<any, void>("/auth/pwrequest", {
      email: email,
      client_id: secrets.SC_CLIENT_ID,
      client_secret: secrets.SC_CLIENT_SECRET
    });
  }

  checkPWRequirements(possiblePass: string): string {
    if (!possiblePass) { return ""; }
    if (possiblePass.length >= 24) {
      const chars = new Map<string, number>();
      for (let i = 0; i < possiblePass.length; i++) {
        const char = possiblePass.charAt(i);
        const count = chars.get(char) || 0;
        chars.set(char, count + 1);
      }
      if (chars.size <= 3) {
        return `Password must contain more than ${chars.size} characters`;
      }
      return "";
    }
    if (possiblePass.length < 10) {
      return "Password must be longer than 10 characters";
    }
    if (!/[A-Z]/.test(possiblePass)) {
      return "Password must contain at least 1 capital letter";
    }
    if (!/[a-z]/.test(possiblePass)) {
      return "Password must contain at least 1 lowercase letter";
    }
    if (!/[0-9]/.test(possiblePass)) {
      return "Password must contain at least 1 number";
    }
    if (!/[*&^%$#@!+=.|\\/<>;:`~'"{}[\]()_-]/.test(possiblePass)) {
      return "Password must contain at least 1 special character";
    }
    return "";
  }

  async getAccountMembers(search?: string, limit?: number): Promise<Array<Membership>> {
    if (!this.currentMembership) {
      return bluebird.reject(
        new Error("Missing Account Info. Have you selected a Company?"));
    }
    const accountID = encodeURIComponent(this.currentMembership.accountID);
    const q: QueryOptions = {};
    if (limit) {
      q.limit = limit.toString();
    }
    if (search) {
      q.q = encodeURIComponent(search);
    }
    const members = await http.get<Array<Membership>, QueryOptions>(
      `/account/${accountID}/member`, q);
    for (const mem of members) {
      mem.avatarText = this.makeAvatarText(mem);
      mem.created = moment(mem.created, moment.ISO_8601);
    }
    return members;
  }

  async getAccountMembersForGroup(groupID: string, search?: string, limit?: number): Promise<Array<Membership>> {
    if (!this.currentMembership) {
      return bluebird.reject(
        new Error("Missing Account Info. Have you selected a Company?"));
    }
    const accountID = encodeURIComponent(this.currentMembership.accountID);
    const groupUri = encodeURIComponent(groupID);
    const q: QueryOptions = {};
    if (limit) {
      q.limit = limit.toString();
    }
    if (search) {
      q.q = encodeURIComponent(search);
    }
    const members = await http.get<Array<Membership>, QueryOptions>(
      `/account/${accountID}/group/${groupUri}/members`, q);
    for (const mem of members) {
      mem.avatarText = this.makeAvatarText(mem);
      mem.created = moment(mem.created, moment.ISO_8601);
    }
    return members;
  }

  async memberFor(userID: string): Promise<Membership | null> {
    let membership: Membership | null = this.acctMemberMap[userID];
    if (membership) { return membership; }
    if (userID in this.acctMemberTasks) {
      return await this.acctMemberTasks[userID];
    }
    const prom = this.fetchMemberFor(userID);
    this.acctMemberTasks[userID] = prom;
    membership = await prom;
    if (!membership) { return null; }
    this.acctMemberMap[userID] = membership;
    delete this.acctMemberTasks[userID];
    return membership;
  }

  private async fetchMemberFor(userID: string): Promise<Membership | null> {
    if (!this.currentMembership) {
      throw new Error("Missing Account Info. Have you selected a Company?");
    }
    const accountID = encodeURIComponent(this.currentMembership.accountID);
    const userUri = encodeURIComponent(userID);
    const membership = await http.get<Membership>(`/account/${accountID}/member/${userUri}`);
    if (!membership) { return null; }
    if (!membership.avatarText) {
      membership.avatarText = this.makeAvatarText(membership);
    }
    return membership;
  }

  async membersFor(userIDs: Set<string>): Promise<{ [id: string]: Membership }> {
    if (userIDs.size <= 0) { return {}; }
    const missing: Array<string> = [];
    for (const userID of userIDs) {
      if (!this.acctMemberMap[userID]) {
        missing.push(userID);
      }
    }
    if (missing.length <= 0) { return this.acctMemberMap; }

    if (!this.currentMembership) {
      throw new Error("Missing Account Info. Have you selected a Company?");
    }
    const accountID = encodeURIComponent(this.currentMembership.accountID);
    const members = await http.post<{ userIDs: Array<string> }, Array<Membership>>(
      `/account/${accountID}/member/`, { userIDs: missing });

    for (const membership of members) {
      if (!membership.avatarText) {
        membership.avatarText = this.makeAvatarText(membership);
      }
      this.acctMemberMap[membership.userID] = membership;
    }
    return this.acctMemberMap;
  }

  async updateMembership(forUserID: string, membership: Partial<Membership>): Promise<Membership> {
    if (!this.currentMembership) {
      return bluebird.reject(
        new Error("Missing Account Info. Have you selected a Company?"));
    }
    membership.userID = forUserID;
    const accountID = encodeURIComponent(this.currentMembership.accountID);
    const userUri = encodeURIComponent(forUserID);
    const m = await http.post<Partial<Membership>, Membership>(
      `/account/${accountID}/member/${userUri}`, membership);
    if (m.userID === this.currentMembership.userID) {
      this.setButtonText(m);
      m.regionID = this.currentRegion.id;
      if (m.accountID === this.currentMembership.accountID) {
        this.currentMembership = m;
      }
      for (let i = 0; i < this.memberships.length; i++) {
        const mem = this.memberships[i];
        if (mem.userID === m.userID && mem.accountID === m.accountID) {
          this.memberships[i] = m;
          await this.saveMembershipsAndState(m.userID, this.currentMembership, this.memberships);
          break;
        }
      }
    }
    m.created = moment(m.created, moment.ISO_8601);
    m.avatarText = this.makeAvatarText(m);
    return m;
  }

  deleteMembership(forUserID: string): Promise<void> {
    if (!this.currentMembership) {
      return bluebird.reject(
        new Error("Missing Account Info. Have you selected a Company?"));
    }
    const accountID = encodeURIComponent(this.currentMembership.accountID);
    const userUri = encodeURIComponent(forUserID);
    return http.delete(`/account/${accountID}/member/${userUri}/`);
  }

  async getMemberDevices(userID: string): Promise<Array<Device>> {
    if (!this.currentMembership) {
      return bluebird.reject(
        new Error("Missing Account Info. Have you selected a Company?"));
    }
    const accountID = encodeURIComponent(this.currentMembership.accountID);
    const uID = encodeURIComponent(userID);
    const devices = await http.get<Array<Device>>(
      `/account/${accountID}/member/${uID}/devices`);
    for (const dev of devices) {
      if (dev.created) { dev.created = moment(dev.created, moment.ISO_8601); }
      if (dev.modified) { dev.modified = moment(dev.modified, moment.ISO_8601); }
    }
    return devices;
  }

  async deleteDevice(deviceID: string): Promise<void> {
    const deviceUri = encodeURIComponent(deviceID);
    await http.delete(`/device/${deviceUri}`);
  }

  sendInvite(req: InviteRequest): Promise<InviteResponse> {
    if (!this.currentMembership) {
      return bluebird.reject(
        new Error("Missing Account Info. Have you selected a Company?"));
    }
    req.client_id = secrets.SC_CLIENT_ID;
    const accountID = encodeURIComponent(this.currentMembership.accountID);
    return http.post<InviteRequest, InviteResponse>(`/account/${accountID}/invite`, req);
  }

  validateToken(token: string): Promise<AccountInvite> {
    try {
      const t = this.unauthDecode(token); // will throw if there's an issue
      if (t.exp && Date.now() >= t.exp * 1000) {
        return bluebird.reject(
          new ExpiredTokenError("This invite has expired. Please, request a new one or login.", 401));
      }
    } catch (err) {
      void err;
      return bluebird.reject(new Error(
        "This token appears to be invalid. Please, ensure the link was copied correctly."));
    }
    return http.post<{ token: string }, AccountInvite>("/auth/validate_token", { token: token });
  }

  validateInviteCode(code: string): Promise<{ invite: AccountInvite, token: string }> {
    return http.post<{ code: string }, { invite: AccountInvite, token: string }>(
      "/auth/validate_code", { code: code });
  }

  async getAccountInvites(accountID: string): Promise<Array<AccountInvite>> {
    const accountUri = encodeURIComponent(accountID);
    const invites = await http.get<Array<AccountInvite>>(`/account/${accountUri}/invite/`);
    for (const invite of invites) {
      if (invite.validAfter) {
        invite.validAfter = DateTime.fromISO(invite.validAfter as unknown as string);
      }
      invite.validBefore = DateTime.fromISO(invite.validBefore as unknown as string);
    }
    return invites;
  }

  async createUser(newuser: NewUser, accountID: string, inviteToken: string): Promise<void> {
    accountID = encodeURIComponent(accountID);
    const cryptoService = cryptoServiceFactory();
    const keys = cryptoService.generateNewKeyPair();
    newuser.publicKey = bufferToBase64(cryptoService.publicKeyToBuffer(keys.publicKey));
    const result = await http.post<NewUser, TokenData>(`/account/${accountID}/user/`, newuser, inviteToken);
    await this.clearAuthData(this.currentRegion);
    this.lastSync = moment().subtract(3, "hours");
    this.memberships = [];
    this.currentMembership = null;
    if (result.enc) {
      const data = JSON.parse(new TextDecoder().decode(
        cryptoService.decapsulate(result.enc, keys.privateKey))) as TokenData;
      await this.processToken(data, false);
    } else {
      await this.processToken(result, false);
    }
  }

  async claimInvite(inviteToken: string): Promise<void> {
    const cryptoService = cryptoServiceFactory();
    const keys = cryptoService.generateNewKeyPair();
    const result = await http.post<{ token: string, publicKey: string }, TokenData | null>(
      "/account/claim/", {
      token: inviteToken,
      publicKey: bufferToBase64(cryptoService.publicKeyToBuffer(keys.publicKey))
    });

    if (result) {
      await this.clearAuthData(this.currentRegion);
      if (result.enc) {
        const data = JSON.parse(new TextDecoder().decode(
          cryptoService.decapsulate(result.enc, keys.privateKey))) as TokenData;
        await this.processToken(data, this.needAuthMethod);
      } else {
        await this.processToken(result, this.needAuthMethod);
      }
    }
  }

  async deleteInvite(accountID: string, inviteID: string): Promise<void> {
    accountID = encodeURIComponent(accountID);
    inviteID = encodeURIComponent(inviteID);
    await http.delete(`/account/${accountID}/invite/${inviteID}`);
  }

  async claimOneTimeToken(token: string): Promise<void> {
    try {
      this.unauthDecode(token); // will throw if there's an issue
    } catch (err) {
      void err;
      throw new Error("Invalid Token");
    }
    this.refresh_token = token;
    this.tokenExpires = moment.tz().subtract(10, "seconds");
    this.needAuthMethod = false;

    await this.refresh_middleware("");
  }

  async createTempUser(accountID: string, meetingID: string, meetingToken: string,
    firstname: string, lastname: string): Promise<void> {
    accountID = encodeURIComponent(accountID);
    const cryptoService = cryptoServiceFactory();
    const keys = cryptoService.generateNewKeyPair();
    const publicKey = bufferToBase64(cryptoService.publicKeyToBuffer(keys.publicKey));

    const result = await http.post<AnonMeetingMemberBody, TokenData>(`/account/${accountID}/meeting_guest/`, {
      clientID: secrets.SC_CLIENT_ID,
      clientSecret: secrets.SC_CLIENT_SECRET,
      meetingID: meetingID,
      meetingToken: meetingToken,
      firstname: firstname,
      lastname: lastname,
      publicKey: publicKey
    });

    await this.clearAuthData(this.currentRegion);
    this.lastSync = moment().subtract(3, "hours");
    this.memberships = [];
    this.currentMembership = null;
    if (result.enc) {
      const data = JSON.parse(new TextDecoder().decode(
        cryptoService.decapsulate(result.enc, keys.privateKey))) as TokenData;
      await this.processToken(data, true);
    } else {
      await this.processToken(result, true);
    }

    // expect getAccountMemberships to be called after this
  }

  async createTempMember(accountID: string, meetingID: string, meetingToken: string): Promise<Membership> {
    accountID = encodeURIComponent(accountID);
    const cryptoService = cryptoServiceFactory();
    const keys = cryptoService.generateNewKeyPair();
    const publicKey = bufferToBase64(cryptoService.publicKeyToBuffer(keys.publicKey));

    const result = await http.post<
      Omit<AnonMeetingMemberBody, "firstname" | "lastname">,
      TokenData & { membership: Membership }>(
        `/account/${accountID}/meeting_member/`, {
        clientID: secrets.SC_CLIENT_ID,
        clientSecret: secrets.SC_CLIENT_SECRET,
        meetingID: meetingID,
        meetingToken: meetingToken,
        publicKey: publicKey
      }
      );

    //this.clearAuthData(this.currentRegion);
    //this.lastSync = moment().subtract(3, "hours");
    //this.memberships = [];
    const membership = result.membership;
    membership.avatarText = this.makeAvatarText(membership);
    this.memberships.push(membership);
    if (result.enc) {
      const data = JSON.parse(new TextDecoder().decode(
        cryptoService.decapsulate(result.enc, keys.privateKey))) as TokenData;
      await this.processToken(data, this.needAuthMethod);
    } else {
      await this.processToken(result, this.needAuthMethod);
    }
    membership.regionID = this.currentRegion.id;
    // use this.currentMembership below because we want the UI to change it later when it's ready
    await this.saveMembershipsAndState(membership.userID, this.currentMembership, this.memberships);
    membership.created = moment(membership.created, moment.ISO_8601);
    return membership;
  }

  async createLocalCredential(
    userID: string, email: string): Promise<ThirdPartyAuth> {
    const challengeBuf = new Uint8Array(await bHttp.post("/authn/challenge", {
      email: email,
      userID: userID,
      client_id: secrets.SC_CLIENT_ID,
      client_secret: secrets.SC_CLIENT_SECRET
    }));
    const challengeData = deserialize(challengeBuf,
      { promoteBuffers: true, promoteValues: true }) as ChallengeResponse;

    const resp = await (navigator.credentials.create({
      publicKey: {
        challenge: challengeData.challenge,
        rp: challengeData.rp,
        user: {
          id: new TextEncoder().encode(userID),
          name: email,
          displayName: email
        },
        pubKeyCredParams: [
          //{alg: -257, type: "public-key"},
          { alg: -7, type: "public-key" }
        ],
        authenticatorSelection: {
          userVerification: "required",
          residentKey: "preferred",
          requireResidentKey: false
        },
        timeout: 75000,
        attestation: "direct"
      }
    }) as Promise<PublicKeyCredential | null>);

    if (!resp) { throw new Error("No response from authenticator"); }

    console.log("response:", resp);
    return await bHttp.postBufJSON<ThirdPartyAuth>("/authn/register", serialize({
      id: resp.id,
      rawId: new Uint8Array(resp.rawId),
      attachment: resp.authenticatorAttachment,
      response: {
        clientDataJSON: new Uint8Array(resp.response.clientDataJSON),
        attestationObject: new Uint8Array(
          (resp.response as AuthenticatorAttestationResponse).attestationObject)
      }
    }));
  }

  async getAuthOptions(email: string): Promise<ChallengeResponse> {
    const buf = new Uint8Array(await bHttp.post("/authn/challenge", {
      email: email,
      client_id: secrets.SC_CLIENT_ID,
      client_secret: secrets.SC_CLIENT_SECRET
    }));
    return deserialize(buf,
      { promoteBuffers: true, promoteValues: true }) as ChallengeResponse;
  }

  async assertLocalCredential(auths: Array<ThirdPartyAuth>,
    challenge: ArrayBuffer, rpID: string, token: string): Promise<void> {

    console.log("auths to assert:", auths);
    const creds: Array<{ type: "public-key", id: Uint8Array, transports: AuthenticatorTransport[] }> = [];
    for (const auth of auths) {
      if (auth.idtype === AuthType.webauthn) {
        creds.push({
          type: "public-key",
          id: auth.identity as Uint8Array,
          transports: auth.attachment === "cross-platform" ?
            ["usb", "ble", "nfc", "hybrid"] :
            ["internal"]
        });
      }
    }
    console.log("assertLocalCredential: using relying party:", rpID);
    const req: CredentialRequestOptions = {
      publicKey: {
        challenge: challenge,
        rpId: rpID,
        allowCredentials: creds,
        timeout: 60000,
        userVerification: "required"
      }
    };

    const authResult = await (
      navigator.credentials.get(req) as Promise<PublicKeyCredential | null>);

    if (!authResult) { throw new Error("No response from authenticator"); }
    console.log("result:", authResult);
    const response = authResult.response as AuthenticatorAssertionResponse;

    const result = await bHttp.postBufJSON<TokenData>("/authn/login", serialize({
      id: authResult.id,
      rawId: new Uint8Array(authResult.rawId),
      type: authResult.type,
      response: {
        clientDataJSON: new Uint8Array(authResult.response.clientDataJSON),
        authenticatorData: new Uint8Array(response.authenticatorData),
        signature: new Uint8Array(response.signature),
        userHandle: response.userHandle ? new Uint8Array(response.userHandle) : undefined
      }
    }), token);
    if (!result) {
      throw new Error("No Response from Server");
    }
    await this.clearAuthData(this.currentRegion);
    await this.processToken(result, false);
  }

  getOTPSecret(otpType: "authenticator", email: string): Promise<{ uri: string, secret: string }> {
    return http.post<{ otpType: string, email: string }, { uri: string, secret: string }>(
      "/otp/generate/", { otpType: otpType, email: email });
  }

  confirmOTP(otpType: "authenticator", email: string, otp: string, secret: string): Promise<ThirdPartyAuth> {
    return http.post<{ otpType: string, email: string, secret: string, otp: string }, ThirdPartyAuth>(
      "/otp/confirm/", { otpType: otpType, email: email, secret: secret, otp: otp });
  }

  updateUserValue(key: string, value: string): Promise<void> {
    return http.post<any, void>("/user/", { [key]: value });
  }

  async changePassword(current: string, newpass: string): Promise<void> {
    const cryptoService = cryptoServiceFactory();
    const keys = cryptoService.generateNewKeyPair();
    const publicKey = bufferToBase64(cryptoService.publicKeyToBuffer(keys.publicKey));

    const result = await http.post<{ current: string, newpass: string, clientID: string, publicKey: string }, TokenData>(
      "/auth/pw/", { current: current, newpass: newpass, clientID: secrets.SC_CLIENT_ID, publicKey: publicKey });
    this.needAuthMethod = false;
    if (result.enc) {
      const data = JSON.parse(new TextDecoder().decode(
        cryptoService.decapsulate(result.enc, keys.privateKey))) as TokenData;
      await this.processToken(data, false);
    } else if (result.access_token) {
      await this.processToken(result, false);
    }
  }

  addEmail(email: string): Promise<void> {
    return http.post<{ email: string }, void>("/user/email", { email });
  }
  removeEmail(email: string): Promise<void> {
    return http.delete<void>(`/user/email/${encodeURIComponent(email)}`);
  }

  removeAuth(authID: string): Promise<void> {
    return http.delete(`/auth/authenticator/${encodeURIComponent(authID)}`);
  }
  updateAuth(authID: string, auth: { authname: string }): Promise<void> {
    return http.post(`/auth/authenticator/${encodeURIComponent(authID)}`, auth);
  }

  async uploadAvatar(membership: Membership, contentType: string, imageData: Uint8Array): Promise<void> {
    const accountUri = encodeURIComponent(membership.accountID);
    const userUri = encodeURIComponent(membership.userID);
    const resp = await http.post<AvatarPostBody, AvatarPostResponse>(
      `/account/${accountUri}/member/${userUri}/avatarUpload`,
      { contentType: contentType });

    const response = await fetch(resp.uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": contentType
      },
      credentials: "omit",
      body: imageData
    });

    const data = await response.text();
    if (!response.ok) {
      console.error("Couldn't upload image:", response, data);
      throw new Error("Failed uploading image");
    }

    membership.avatarUrl = resp.avatarUrl;
    if (this.currentMembership && membership.userID === this.currentMembership.userID) {
      if (membership.accountID === this.currentMembership.accountID) {
        this.currentMembership = membership;
      }
      for (let i = 0; i < this.memberships.length; i++) {
        const mem = this.memberships[i];
        if (mem.userID === membership.userID && mem.accountID === membership.accountID) {
          this.memberships[i].avatarUrl = resp.avatarUrl;
          await this.saveMembershipsAndState(mem.userID, this.currentMembership, this.memberships);
          break;
        }
      }
    }
  }

  makeAvatarText(acctMember: Membership | User): string {
    if (acctMember.firstname && acctMember.lastname) {
      return acctMember.firstname.charAt(0) + acctMember.lastname.charAt(0);
    } else if (acctMember.lastname) {
      return acctMember.lastname.charAt(0) + acctMember.lastname.charAt(1);
    } else if (acctMember.firstname) {
      return acctMember.firstname.charAt(0) + acctMember.firstname.charAt(1);
    } else {
      return "XX";
    }
  }

  getAccountInfo(accountID: string): Promise<Account> {
    const accountUri = encodeURIComponent(accountID);
    return http.get(`/account/${accountUri}/info`);
  }

  updateAccountInfo(accountID: string, update: Partial<Account>): Promise<Account> {
    const accountUri = encodeURIComponent(accountID);
    update._id = accountID;
    return http.put<Partial<Account>, Account>(`/account/${accountUri}/info`, update);
  }

  private unauthDecode(token: string): JwtPayload {
    return jwtDecode<JwtPayload>(token);
  }

  updateDeviceName(deviceID: string, name: string): Promise<{ id: string, displayName: string }> {
    return http.put(`/device/${encodeURIComponent(deviceID)}`, { displayName: name });
  }

}

interface AvatarPostBody {
  contentType: string
}
interface AvatarPostResponse {
  uploadUrl: string,
  avatarUrl: string
}
interface TokenData {
  access_token: string,
  refresh_token: string,
  expires_in: number,
  perms: UserScopes,
  enc?: string
}


let authService: AuthService;

export function authServiceFactory(): AuthService {
  if (!authService) {
    authService = new AuthServiceImpl();
  }
  return authService;
}

